/**
 * The card under the Chi tiêu / Thu nhập tabs: a donut plus the list it is a picture of.
 *
 * One component for both tabs because they are the same view of two breakdowns — spending by
 * category, or money in by source. Every figure comes from `app.money_flow` already summed;
 * this file decides only what is shown and in what order.
 *
 * The list is also the chart's legend and its accessible equivalent: each slice's colour is a
 * dot next to a row that spells the name and the amount out in text.
 */
import { lazy, Suspense, useId, useState } from 'react';
import { Link } from 'react-router';

import {
  toDonutSlices,
  type ExpenseBreakdown,
  type MoneyIn,
  type ShareSlice,
} from '@pfo/analytics';
import type { Period } from '@pfo/shared';

import { cx } from '../../../shared/lib/cx.js';
import { formatVnd } from '../../../shared/lib/money.js';
import lists from '../../../shared/styles/lists.module.css';
import { resolveCategoryEmoji } from '../../../shared/ui/icons/categoryEmoji.js';
import { LoadingState } from '../../../shared/ui/LoadingState.js';
import { restColor, seriesColor, type ChartTheme } from '../model/chartPalette.js';
import { STABILITY_LABELS, UNCATEGORISED_LABEL } from '../model/homeLabels.js';
import styles from './BreakdownCard.module.css';
import { EmptyBreakdown } from './EmptyBreakdown.js';
import type { SummaryTab } from './SummaryTabs.js';

/**
 * ECharts is the heaviest thing on the screen and nothing above the fold needs it, so it
 * arrives in its own chunk. Quick Add — the feature with a 5-second budget — never pays for it.
 */
const ExpenseDonut = lazy(() =>
  import('./ExpenseDonut.js').then((module) => ({ default: module.ExpenseDonut })),
);

/** Five named slices then the tail, which is how the reference screen groups them. */
const MAX_NAMED_SLICES = 5;

const REST_LABEL = 'Còn lại';

/** Rows shown before the list has to be asked to open — one more than the donut names. */
const VISIBLE_ROWS = 6;

type CategoryTab = 'child' | 'parent';

const CATEGORY_TABS: ReadonlyArray<{ id: CategoryTab; label: string }> = [
  { id: 'child', label: 'Danh mục con' },
  { id: 'parent', label: 'Danh mục cha' },
];

type Row = ShareSlice & {
  /** Present on spending rows: where tapping the row leads. */
  href?: string;
  meta: string;
};

export function BreakdownCard({
  mode,
  id,
  expense,
  moneyIn,
  period,
  theme,
}: {
  mode: SummaryTab;
  /** Matches the `aria-controls` of the Chi tiêu / Thu nhập tabs. */
  id: string;
  expense: ExpenseBreakdown;
  moneyIn: MoneyIn;
  period: Period;
  theme: ChartTheme;
}) {
  const [categoryTab, setCategoryTab] = useState<CategoryTab>('child');
  const [showAll, setShowAll] = useState(false);
  const headingId = useId();

  const isExpense = mode === 'expense';
  const total = isExpense ? expense.total : moneyIn.total;
  const rows = isExpense
    ? expenseRows(categoryTab === 'child' ? expense.byCategory : expense.byParent, period)
    : incomeRows(moneyIn);

  const slices = toDonutSlices(rows, { maxSlices: MAX_NAMED_SLICES });
  // A household with thirty categories made the dashboard seven screens tall (measured on a
  // Pixel 7, 2026-08-19). The named slices are shown, the tail is one tap away.
  const visibleRows = showAll ? rows : rows.slice(0, VISIBLE_ROWS);
  const hiddenCount = rows.length - visibleRows.length;

  return (
    <section
      className={lists.card}
      id={id}
      role="tabpanel"
      aria-labelledby={headingId}
      // The heading names the panel; a duplicated aria-label would be announced twice.
    >
      <h2 className={lists.cardTitle} id={headingId}>
        {isExpense ? 'Chi theo danh mục' : 'Thu theo nguồn'}
      </h2>

      {total === 0 || slices.length === 0 ? (
        <EmptyBreakdown
          message={
            isExpense
              ? 'Ghi lại các khoản chi giúp bạn thấy tiền đi đâu mỗi tháng.'
              : 'Ghi lại mọi khoản thu giúp bạn theo dõi chính xác tình hình tài chính của mình.'
          }
          actionLabel={isExpense ? 'Nhập giao dịch chi' : 'Nhập giao dịch thu'}
          actionHref={isExpense ? '/them?loai=chi' : '/them?loai=thu'}
        />
      ) : (
        <>
          {/* The fallback is a box of the chart's height: the card must not jump when the
              chunk lands. The spinner inside says it is on its way rather than missing. */}
          <Suspense
            fallback={
              <div className={styles.chartPlaceholder}>
                <LoadingState label="Đang vẽ biểu đồ…" />
              </div>
            }
          >
            <ExpenseDonut slices={slices} theme={theme} restLabel={REST_LABEL} />
          </Suspense>

          {/* Child / parent only makes sense for spending: an income source has no parent. */}
          {isExpense ? (
            <div className={styles.tabs} role="tablist" aria-label="Nhóm chi theo">
              {CATEGORY_TABS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={categoryTab === item.id}
                  className={cx(styles.tab, categoryTab === item.id && styles.tabActive)}
                  onClick={() => setCategoryTab(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          ) : null}

          <ul className={lists.list}>
            {visibleRows.map((row, index) => (
              <li key={row.id ?? 'uncategorised'}>
                <RowBody row={row} index={index} theme={theme} period={period} />
              </li>
            ))}
          </ul>

          {hiddenCount > 0 || showAll ? (
            <button
              type="button"
              className={lists.rowButton}
              onClick={() => setShowAll((open) => !open)}
            >
              {showAll ? 'Thu gọn' : `Xem thêm ${hiddenCount} mục`}
            </button>
          ) : null}
        </>
      )}
    </section>
  );
}

function RowBody({
  row,
  index,
  theme,
  period,
}: {
  row: Row;
  index: number;
  theme: ChartTheme;
  period: Period;
}) {
  const body = (
    <>
      <span className={styles.rowLeft}>
        <span
          className={styles.swatch}
          style={{
            // Beyond the fifth row the colour is the group colour, because that is exactly
            // where the row went in the chart.
            background: index < MAX_NAMED_SLICES ? seriesColor(index, theme) : restColor(theme),
          }}
          aria-hidden="true"
        />
        <span className={styles.icon} aria-hidden="true">
          {row.icon}
        </span>
        <span className={lists.rowText}>
          <span className={lists.rowName}>{row.name ?? UNCATEGORISED_LABEL}</span>
          <span className={lists.rowMeta}>{row.meta}</span>
        </span>
      </span>
      <span className={lists.amount}>{formatVnd(row.amount)}</span>
    </>
  );

  if (row.href === undefined) {
    return <div className={lists.row}>{body}</div>;
  }

  // Same shape as the account rows: the `row` wrapper draws the separator and the padding,
  // the link inside it is the touch target. Without the wrapper the rows ran into each other,
  // and with `rowLeft` not stretching, `space-between` parked the amount in the middle of the
  // row instead of next to the chevron (PO screenshot, 2026-09-17).
  return (
    <div className={lists.row}>
      <Link
        className={lists.rowLink}
        to={row.href}
        aria-label={`Xem giao dịch ${row.name ?? UNCATEGORISED_LABEL} tháng ${period.month}/${period.year}`}
      >
        {body}
        <span className={lists.chevron} aria-hidden="true">
          ›
        </span>
      </Link>
    </div>
  );
}

function expenseRows(slices: ExpenseBreakdown['byCategory'], period: Period): Row[] {
  return slices.map((slice) => ({
    id: slice.categoryId,
    name: slice.name,
    // The SAME resolver Quick Add and the category screen use: `categories.icon` is empty for
    // every seeded category, and the emoji is derived from the name there. Reading the column
    // straight showed a generic tag on the dashboard for a category the user sees as 🍜.
    icon: resolveCategoryEmoji({ icon: slice.icon, name: slice.name ?? UNCATEGORISED_LABEL }),
    amount: slice.amount,
    sharePct: slice.sharePct,
    meta: `${slice.sharePct}%`,
    href: drillDownHref(slice.categoryId, period),
  }));
}

function incomeRows(moneyIn: MoneyIn): Row[] {
  return moneyIn.bySource.map((source) => ({
    id: source.incomeSourceId,
    name: source.name,
    // Income sources have no icon column; the resolver's name rules already cover them
    // (lương 💼, thưởng 🏅, hoàn thuế 🧾 — decision N2).
    icon: resolveCategoryEmoji({ icon: null, name: source.name }),
    amount: source.amount,
    sharePct: source.sharePct,
    meta: `${source.sharePct}% · ${STABILITY_LABELS[source.stabilityClass] ?? ''}`,
    // No drill-down: the timeline filters by category and account, not by income source.
  }));
}

/** The timeline, filtered to this category and this month. */
function drillDownHref(categoryId: string | null, period: Period): string {
  const params = new URLSearchParams({
    nam: String(period.year),
    thang: String(period.month),
  });
  if (categoryId !== null) params.set('danh-muc', categoryId);
  return `/giao-dich?${params.toString()}`;
}
